/**
 * Terms of Service Page
 * Required for Stripe payment compliance
 */
import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import Footer from "@/components/Footer";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="bg-primary/5 border-b border-primary/10">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Link to="/" className="inline-flex items-center text-primary hover:text-primary/80 mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-serif font-semibold text-charcoal">Terms of Service</h1>
              <p className="text-muted-foreground">Last updated: January 2025</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 max-w-4xl mx-auto px-4 py-12">
        <div className="prose prose-slate max-w-none">
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-charcoal mb-4">1. Acceptance of Terms</h2>
            <p className="text-muted-foreground">
              By accessing or using DuoLyst, you agree to be bound by these Terms of Service. 
              If you do not agree to these terms, please do not use our service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-charcoal mb-4">2. Description of Service</h2>
            <p className="text-muted-foreground">
              DuoLyst is a bucket list and travel planning application that allows users to create, 
              share, and track their travel goals with partners, friends, and groups. We offer both 
              free and premium subscription plans.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-charcoal mb-4">3. User Accounts</h2>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>You must provide accurate and complete information when creating an account</li>
              <li>You are responsible for maintaining the security of your account</li>
              <li>You must be at least 13 years old to use this service</li>
              <li>One person may not maintain more than one account</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-charcoal mb-4">4. Subscriptions and Payments</h2>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Premium features require a paid subscription</li>
              <li>Subscriptions automatically renew unless cancelled</li>
              <li>Payments are processed securely through Stripe</li>
              <li>Refunds are available within 14 days of purchase if you haven't used premium features</li>
              <li>You can cancel your subscription at any time from your account settings</li>
              <li>Prices may change with 30 days notice</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-charcoal mb-4">5. User Content</h2>
            <p className="text-muted-foreground mb-4">
              You retain ownership of content you create. By posting content, you grant us a license to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Display your content to your partners and group members</li>
              <li>Store and backup your content</li>
              <li>Use aggregated, anonymized data to improve our service</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-charcoal mb-4">6. Prohibited Conduct</h2>
            <p className="text-muted-foreground mb-4">
              You agree not to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Use the service for any illegal purpose</li>
              <li>Upload malicious content or spam</li>
              <li>Attempt to gain unauthorized access to other accounts</li>
              <li>Interfere with or disrupt the service</li>
              <li>Scrape or collect user data without permission</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-charcoal mb-4">7. Termination</h2>
            <p className="text-muted-foreground">
              We may terminate or suspend your account at any time for violations of these terms. 
              You may delete your account at any time. Upon termination, your right to use the 
              service ceases immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-charcoal mb-4">8. Disclaimers</h2>
            <p className="text-muted-foreground">
              The service is provided "as is" without warranties of any kind. We do not guarantee 
              that the service will be uninterrupted or error-free. Travel suggestions are for 
              informational purposes only.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-charcoal mb-4">9. Limitation of Liability</h2>
            <p className="text-muted-foreground">
              To the maximum extent permitted by law, DuoLyst shall not be liable for any indirect, 
              incidental, special, consequential, or punitive damages resulting from your use of the service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-charcoal mb-4">10. Changes to Terms</h2>
            <p className="text-muted-foreground">
              We may modify these terms at any time. We will notify users of significant changes 
              via email or through the service. Continued use after changes constitutes acceptance.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-charcoal mb-4">11. Contact</h2>
            <p className="text-muted-foreground">
              For questions about these Terms, contact us at:{" "}
              <a href="mailto:contact@contact.duolyst.co.uk" className="text-primary hover:underline">
                contact@contact.duolyst.co.uk
              </a>
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
